'use client'
import * as React from 'react'

export interface CategoryDataContext {
  categories: Collapsible[]
  open: Collapsible[]
  setOpen: React.Dispatch<React.SetStateAction<Collapsible[]>>
}
export type Collapsible = string

type ContextProps = React.PropsWithChildren<{
  categories: Collapsible[]
  defaultOpen?: Collapsible[]
}>

const ProductDataContext = React.createContext<CategoryDataContext>(null!)

export function ProductDataContextProvider({ children, categories, defaultOpen }: ContextProps) {
  const [open, setOpen] = React.useState<Collapsible[]>(defaultOpen ?? [])

  return (
    <ProductDataContext.Provider
      value={{
        categories,
        open,
        setOpen,
      }}
    >
      {React.useMemo(() => children, [children])}
    </ProductDataContext.Provider>
  )
}

export function useCategoriesDataContext() {
  return React.useContext(ProductDataContext)
}

export function useOpenCategories() {
  const { open } = useCategoriesDataContext()
  return open
}

export function useIsCategoryOpen(category: Collapsible) {
  const { open } = useCategoriesDataContext()
  return open.includes(category)
}

export function useSetOpen() {
  const { setOpen } = useCategoriesDataContext()
  return setOpen
}

export function useToggleCategory(category: Collapsible) {
  const { setOpen } = useCategoriesDataContext()
  const expandCategory = React.useCallback(() => {
    setOpen(open =>
      open.includes(category) ? open.filter(c => c !== category) : [...open, category],
    )
  }, [setOpen, category])

  return expandCategory
}

export function useExpandAll() {
  const { setOpen, categories } = useCategoriesDataContext()

  const expandAll = React.useCallback(() => {
    setOpen(categories)
  }, [setOpen, categories])

  return expandAll
}

export function useCollapseAll() {
  const { setOpen } = useCategoriesDataContext()

  const collapseAll = React.useCallback(() => {
    setOpen([])
  }, [setOpen])

  return collapseAll
}
